import React from 'react';
import { cn } from '@/lib/utils';
import { Plus, Check } from 'lucide-react';

export interface ActionCardProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether the card is in a selected/active state */
  selected?: boolean;
  /** Show a plus icon before the text (for "Add" actions) */
  showPlusIcon?: boolean;
  /** Size variant */
  size?: 'default' | 'sm';
  /** Use half width (matches Yes/No button width when used solo) */
  halfWidth?: boolean;
}

/**
 * ActionCard - A unified button component for Yes/No toggles and Add actions.
 */
export const ActionCard = React.forwardRef<HTMLButtonElement, ActionCardProps>(
  ({ className, children, selected, showPlusIcon, size = 'default', halfWidth, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          'rounded-xl border transition-all duration-300 font-medium relative overflow-hidden group',
          halfWidth ? 'w-1/2' : 'flex-1',
          size === 'default' ? 'py-4 px-6 text-sm font-semibold' : 'py-3 px-4 text-sm',
          selected
            ? 'border-primary bg-white/90 backdrop-blur-sm text-gray-900 shadow-[0_4px_20px_rgba(20,184,166,0.15)] scale-[1.02] border-2'
            : 'border-gray-200 hover:border-primary/30 bg-white hover:bg-gray-50 hover:shadow-md hover:scale-[1.01]',
          disabled && 'opacity-60 cursor-not-allowed hover:scale-100 hover:border-gray-200 hover:shadow-none hover:bg-white',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          className
        )}
        {...props}
      >
        <span className="flex items-center justify-center gap-2 relative z-10">
          {showPlusIcon && <Plus className="h-5 w-5" />}
          {children}
          {selected && !showPlusIcon && <Check className="h-5 w-5 ml-1 animate-in zoom-in duration-300 text-primary" />}
        </span>
      </button>
    );
  }
);

ActionCard.displayName = 'ActionCard';
